// Synthetic night-time camera feed for StreamDescriptor.kind === 'sim' (no NX connected).
// The scene is drawn once per seed (fence, lot, buildings, light poles, cars, vignette, scanlines);
// film grain is animated on top unless the user prefers reduced motion.

import { useEffect, useMemo, useRef, useState } from 'react';
import { usePrefersReducedMotion } from '../hooks.ts';
import { fmtDate, fmtTime, tzAbbr } from '../time.ts';

const W = 640;
const H = 360;
const GRAIN_FRAMES = 4;
const GRAIN_FPS = 12;

function hash(str: string): number {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return h >>> 0;
}

function rng(seed: string): () => number {
  let a = hash(seed);
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const grey = (v: number, a = 1) => `rgba(${v},${v},${v},${a})`;

function makeCanvas(): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  return c;
}

function drawScene(seed: string): HTMLCanvasElement {
  const c = makeCanvas();
  const ctx = c.getContext('2d');
  if (!ctx) return c;
  const r = rng(seed);
  const horizon = Math.round(H * (0.34 + r() * 0.12));
  const vpx = W * (0.3 + r() * 0.4);

  // sky
  const sky = ctx.createLinearGradient(0, 0, 0, horizon);
  sky.addColorStop(0, grey(9));
  sky.addColorStop(1, grey(28));
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, W, horizon);

  // ground
  const ground = ctx.createLinearGradient(0, horizon, 0, H);
  ground.addColorStop(0, grey(30));
  ground.addColorStop(1, grey(13));
  ctx.fillStyle = ground;
  ctx.fillRect(0, horizon, W, H - horizon);

  // tree line silhouette
  ctx.fillStyle = grey(14);
  ctx.beginPath();
  ctx.moveTo(0, horizon);
  let th = 8 + r() * 10;
  for (let x = 0; x <= W; x += 6) {
    th = Math.max(3, Math.min(30, th + (r() - 0.5) * 7));
    ctx.lineTo(x, horizon - th);
  }
  ctx.lineTo(W, horizon);
  ctx.closePath();
  ctx.fill();

  // buildings
  const nb = 1 + Math.floor(r() * 3);
  for (let i = 0; i < nb; i++) {
    const bw = 70 + r() * 130;
    const bh = 30 + r() * 70;
    const bx = r() * (W - bw);
    ctx.fillStyle = grey(18 + Math.floor(r() * 8));
    ctx.fillRect(bx, horizon - bh, bw, bh);
    ctx.fillStyle = grey(24);
    ctx.fillRect(bx, horizon - bh, bw, 3);
    // windows / dock doors
    const rows = Math.max(1, Math.floor(bh / 22));
    const cols = Math.max(2, Math.floor(bw / 24));
    for (let ry = 0; ry < rows; ry++) {
      for (let cx = 0; cx < cols; cx++) {
        if (r() < 0.55) continue;
        const lit = r() < 0.25;
        ctx.fillStyle = lit ? grey(120 + Math.floor(r() * 60), 0.85) : grey(36);
        ctx.fillRect(bx + 8 + cx * ((bw - 16) / cols), horizon - bh + 9 + ry * 20, 7, 8);
      }
    }
    // wall-pack light
    if (r() < 0.7) {
      const lx = bx + bw * (0.2 + r() * 0.6);
      const ly = horizon - bh * 0.55;
      const g = ctx.createRadialGradient(lx, ly, 0, lx, ly, 46);
      g.addColorStop(0, 'rgba(235,235,225,.55)');
      g.addColorStop(1, 'rgba(235,235,225,0)');
      ctx.fillStyle = g;
      ctx.fillRect(lx - 46, ly - 46, 92, 92);
    }
  }

  // lot surface and stall lines converging on the vanishing point
  ctx.fillStyle = grey(24, 0.6);
  ctx.beginPath();
  ctx.moveTo(vpx - 40, horizon + 2);
  ctx.lineTo(vpx + 40, horizon + 2);
  ctx.lineTo(W + 80, H);
  ctx.lineTo(-80, H);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = grey(190, 0.16);
  ctx.lineWidth = 1.2;
  const lines = 5 + Math.floor(r() * 5);
  for (let i = -lines; i <= lines; i++) {
    const bx = W / 2 + i * (W / lines) * 0.75;
    const t = 0.55;
    ctx.beginPath();
    ctx.moveTo(bx, H);
    ctx.lineTo(bx + (vpx - bx) * t, H + (horizon - H) * t);
    ctx.stroke();
  }

  // light poles with glow and ground pools
  const np = 1 + Math.floor(r() * 3);
  for (let i = 0; i < np; i++) {
    const depth = 0.12 + r() * 0.5;
    const by = horizon + (H - horizon) * depth;
    const bx = W * (0.08 + r() * 0.84);
    const ph = 50 + depth * 170;
    const ty = by - ph;
    const pool = ctx.createRadialGradient(bx, by, 0, bx, by, 60 + depth * 120);
    pool.addColorStop(0, 'rgba(210,210,200,.22)');
    pool.addColorStop(1, 'rgba(210,210,200,0)');
    ctx.save();
    ctx.translate(bx, by);
    ctx.scale(1, 0.35);
    ctx.translate(-bx, -by);
    ctx.fillStyle = pool;
    ctx.fillRect(bx - 200, by - 200, 400, 400);
    ctx.restore();
    ctx.strokeStyle = grey(44);
    ctx.lineWidth = 1.5 + depth * 2;
    ctx.beginPath();
    ctx.moveTo(bx, by);
    ctx.lineTo(bx, ty);
    ctx.lineTo(bx + 10 + depth * 10, ty);
    ctx.stroke();
    const lx = bx + 10 + depth * 10;
    const glow = ctx.createRadialGradient(lx, ty + 2, 0, lx, ty + 2, 30 + depth * 40);
    glow.addColorStop(0, 'rgba(255,255,245,.9)');
    glow.addColorStop(0.15, 'rgba(240,240,230,.45)');
    glow.addColorStop(1, 'rgba(240,240,230,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(lx - 80, ty - 78, 160, 160);
  }

  // parked cars
  const nc = Math.floor(r() * 5);
  for (let i = 0; i < nc; i++) {
    const depth = 0.25 + r() * 0.6;
    const cy = horizon + (H - horizon) * depth;
    const s = 0.4 + depth * 0.9;
    const cx = W * (0.05 + r() * 0.85);
    const cw = 70 * s;
    const ch = 22 * s;
    ctx.fillStyle = grey(34 + Math.floor(r() * 18));
    ctx.beginPath();
    ctx.roundRect(cx, cy - ch, cw, ch, 4 * s);
    ctx.fill();
    ctx.fillStyle = grey(22);
    ctx.beginPath();
    ctx.roundRect(cx + cw * 0.2, cy - ch * 1.6, cw * 0.55, ch * 0.7, 5 * s);
    ctx.fill();
    ctx.fillStyle = grey(70, 0.35);
    ctx.fillRect(cx + cw * 0.25, cy - ch * 1.5, cw * 0.45, ch * 0.08);
    ctx.fillStyle = grey(8);
    ctx.fillRect(cx + cw * 0.12, cy - 2 * s, 10 * s, 4 * s);
    ctx.fillRect(cx + cw * 0.74, cy - 2 * s, 10 * s, 4 * s);
  }

  // chain-link fence across the mid ground
  if (r() < 0.85) {
    const fy = horizon + (H - horizon) * (0.18 + r() * 0.25);
    const fh = 34 + r() * 26;
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, fy - fh, W, fh);
    ctx.clip();
    ctx.strokeStyle = grey(150, 0.07);
    ctx.lineWidth = 1;
    for (let x = -fh; x < W + fh; x += 7) {
      ctx.beginPath();
      ctx.moveTo(x, fy);
      ctx.lineTo(x + fh, fy - fh);
      ctx.moveTo(x, fy - fh);
      ctx.lineTo(x + fh, fy);
      ctx.stroke();
    }
    ctx.restore();
    ctx.strokeStyle = grey(95, 0.75);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, fy - fh);
    ctx.lineTo(W, fy - fh);
    ctx.stroke();
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, fy);
    ctx.lineTo(W, fy);
    ctx.stroke();
    const step = 48 + r() * 30;
    ctx.lineWidth = 2.5;
    for (let x = r() * step; x < W; x += step) {
      ctx.beginPath();
      ctx.moveTo(x, fy + 2);
      ctx.lineTo(x, fy - fh - 4);
      ctx.stroke();
    }
    // barbed wire
    ctx.strokeStyle = grey(110, 0.35);
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    for (let x = 0; x < W; x += 4) ctx.lineTo(x, fy - fh - 6 + Math.sin(x / 3) * 1.5);
    ctx.stroke();
  }

  // vignette
  const v = ctx.createRadialGradient(W / 2, H / 2, H * 0.25, W / 2, H / 2, W * 0.65);
  v.addColorStop(0, 'rgba(0,0,0,0)');
  v.addColorStop(1, 'rgba(0,0,0,.65)');
  ctx.fillStyle = v;
  ctx.fillRect(0, 0, W, H);

  // faint scanlines
  ctx.fillStyle = 'rgba(255,255,255,.028)';
  for (let y = 0; y < H; y += 3) ctx.fillRect(0, y, W, 1);
  return c;
}

function drawGrain(frame: number): HTMLCanvasElement {
  const c = makeCanvas();
  const ctx = c.getContext('2d');
  if (!ctx) return c;
  const img = ctx.createImageData(W, H);
  const d = img.data;
  const r = rng(`grain-${frame}`);
  for (let i = 0; i < d.length; i += 4) {
    const v = Math.floor(r() * 255);
    d[i] = v;
    d[i + 1] = v;
    d[i + 2] = v;
    d[i + 3] = 20;
  }
  ctx.putImageData(img, 0, 0);
  return c;
}

let grainCache: HTMLCanvasElement[] | null = null;
function grainFrames(): HTMLCanvasElement[] {
  if (!grainCache) grainCache = Array.from({ length: GRAIN_FRAMES }, (_, i) => drawGrain(i));
  return grainCache;
}

/** OSD mode badge; defaults to LIVE / ARCHIVE from the stream mode. */
export interface ModeBadge {
  text: string;
  cls: 'live' | 'archive' | 'alarm';
}

export interface SimFeedProps {
  seed: string;
  label: string;
  mode: 'live' | 'archive';
  /** Clock start (ms since epoch): `at` for archive, now for live. */
  startAt: number;
  timeZone: string;
  cameraName: string;
  /** Freezes the picture and the on-screen clock. */
  paused?: boolean;
  badge?: ModeBadge;
}

/** One static frame of the synthetic scene (camera rail tiles in simulator mode). */
export function SimStill({ seed, label }: { seed: string; label: string }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const base = useMemo(() => drawScene(seed), [seed]);
  useEffect(() => {
    ref.current?.getContext('2d')?.drawImage(base, 0, 0);
  }, [base]);
  return <canvas ref={ref} width={W} height={H} role="img" aria-label={label} />;
}

export function SimFeed({ seed, label, mode, startAt, timeZone, cameraName, paused = false, badge }: SimFeedProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const reduced = usePrefersReducedMotion();
  const base = useMemo(() => drawScene(seed), [seed]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const grain = grainFrames();
    let frame = 0;
    const paint = () => {
      ctx.globalAlpha = 1;
      ctx.drawImage(base, 0, 0);
      const g = grain[frame % grain.length];
      if (g) {
        ctx.globalCompositeOperation = 'screen';
        ctx.drawImage(g, 0, 0);
        ctx.globalCompositeOperation = 'source-over';
      }
      frame++;
    };
    paint();
    if (reduced || paused) return;
    const t = setInterval(paint, 1000 / GRAIN_FPS);
    return () => clearInterval(t);
  }, [base, reduced, paused]);

  // Clock: starts at startAt and advances in real time, minus time spent paused.
  const clock = useRef<{ mountedAt: number; pausedTotal: number; pauseStart: number | null }>({
    mountedAt: Date.now(),
    pausedTotal: 0,
    pauseStart: null,
  });
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const c = clock.current;
    if (paused && c.pauseStart === null) c.pauseStart = Date.now();
    if (!paused && c.pauseStart !== null) {
      c.pausedTotal += Date.now() - c.pauseStart;
      c.pauseStart = null;
    }
  }, [paused]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, []);
  const c = clock.current;
  const shown = startAt + ((c.pauseStart ?? now) - c.mountedAt - c.pausedTotal);
  const b: ModeBadge = badge ?? { text: mode === 'live' ? 'LIVE' : 'ARCHIVE', cls: mode };

  return (
    <div className="feed sim">
      <canvas ref={canvasRef} width={W} height={H} role="img" aria-label={`Simulated ${mode} view of ${cameraName}`} />
      <div className="osd tl">
        <span className={`mode ${b.cls}`}>{b.text}</span>
        <span className="cn">{cameraName}</span>
        {paused && <span className="cn">PAUSED</span>}
      </div>
      <div className="osd tr">
        <span className="vts" aria-live="off">
          {fmtDate(shown, timeZone)} {fmtTime(shown, timeZone)} {tzAbbr(timeZone, shown)}
        </span>
      </div>
      <div className="osd bl">
        <span className="simtag">SIMULATED · no NX connected</span>
      </div>
      {label && <div className="osd br"><span className="cn dim">{label}</span></div>}
    </div>
  );
}
